/**
 * Languages offered in the control-bar selector.
 *
 * R53: this list now DERIVES from the single-source-of-truth matrix
 * (`@coldpress-voice/shared-types/supported-languages`, backed by supported-languages.json), so the
 * kiosk can never offer a language the stack cannot honour. A language appears here iff ElevenLabs
 * v2.5 can voice Gigi's reply (the hard floor — `tier !== 'UNSUPPORTED'`). Two product tiers ship:
 *   - AUTO   — Deepgram nova-3 `multi` auto-detects it; no button needed, but the button still pins.
 *   - PINNED — auto-detect can't hear it; the visitor MUST tap the button so STT re-pins (else the
 *              transcription garbles). The tier travels with each entry so the UI can hint later.
 *
 * `name` is the human label sent to the LLM ("reply only in {name}"); `code` is the BCP-47 tag.
 * `tier` is the matrix tier (AUTO | PINNED) — carried through for future UI affordances.
 */
import {
  LANGUAGE_MATRIX,
  type LangTier,
  ttsModelForLang,
} from '@coldpress-voice/shared-types/supported-languages';

export type Lang = { code: string; name: string; tier: LangTier };

export const AUTO_LANG = 'Auto';

// Derived from the matrix — offered iff Gigi can voice it (UNSUPPORTED excluded). Order preserved
// (AUTO languages first, then PINNED), matching the JSON authoring order.
export const LANGUAGES: Lang[] = LANGUAGE_MATRIX.filter((l) => l.tier !== 'UNSUPPORTED').map(
  (l) => ({
    code: l.code,
    name: l.name,
    tier: l.tier,
  })
);

/** Map a detected BCP-47 code (e.g. "es", "es-419", "zh-CN") to a friendly label for the chip. */
export function labelForCode(code: string | null | undefined): string | null {
  if (!code) return null;
  const base = code.toLowerCase().split('-')[0];
  return (
    LANGUAGES.find((l) => l.code === base)?.name ??
    LANGUAGES.find((l) => l.code === code.toLowerCase())?.name ??
    code
  );
}

/** Resolve the BCP-47 code for a human language NAME (the label sent to the LLM + shown on the chip).
 *  Case-insensitive; null when the name isn't an offered language (e.g. the "Auto" sentinel). */
export function codeForName(name: string | null | undefined): string | null {
  if (!name) return null;
  const n = name.trim().toLowerCase();
  return LANGUAGES.find((l) => l.name.toLowerCase() === n)?.code ?? null;
}

/**
 * R30 V3 — resolve the ElevenLabs TTS model for a language NAME (the value the control-bar switch
 * emits). Bridges name → code → the shared `ttsModelForLang` (the SAME resolver the agent-worker uses,
 * `packages/shared-types` — single source of truth), so the client's optimistic jitter-buffer depth on
 * a set-language switch matches the model the server will actually reconnect the TTS WS with. An
 * unknown/unresolvable name yields a null code → `ttsModelForLang` returns the non-English `*` model
 * (`eleven_multilingual_v2`) — the CONSERVATIVE choice for the buffer (the deepest pre-roll; an
 * unrecognised language is never UNDER-buffered). Never throws.
 */
export function ttsModelForName(name: string | null | undefined): string {
  return ttsModelForLang(codeForName(name));
}
